import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import "./Partner.css";

// ─── Motion presets (same feel as the About page) ───────────────────────────

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

/** For grids: pass `custom={index}` on each child to stagger it. */
const staggerUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: (i) => ({ duration: 0.5, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }),
};

/** CTAs point at the dashboard, so they are plain anchors, not router links. */
function Cta({ cta, variant }) {
  if (!cta) return null;

  const className = `partner-btn partner-btn--${variant}`;
  const isExternal = cta.href.startsWith("http");

  if (isExternal) {
    return (
      <a href={cta.href} target="_blank" rel="noreferrer" className={className}>
        {cta.label} <ArrowRight size={16} />
      </a>
    );
  }

  return (
    <Link to={cta.href} className={className}>
      {cta.label} <ArrowRight size={16} />
    </Link>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`partner-faq-item${isOpen ? " partner-faq-item--open" : ""}`}>
      <button type="button" className="partner-faq-q" onClick={onToggle} aria-expanded={isOpen}>
        {item.q}
        <ChevronDown size={18} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="partner-faq-a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            <p>{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Shared layout for the two partner landing pages. Both pass the same content
 * shape, so /become-seller and /become-merchant stay visually identical and
 * only their copy, photos, and CTA targets differ.
 */
export default function PartnerPage({ content }) {
  const { hero, strip, why, gettingStarted, steps, checklist, band, cards, faq, contact } = content;
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="page-navbar-space partner-page">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className={`partner-hero${hero.bgImage ? " partner-hero--photo" : ""}`}>
        {hero.bgImage && (
          <>
            <img src={hero.bgImage} alt="" aria-hidden="true" className="partner-hero-bg" />
            <div className="partner-hero-overlay" />
          </>
        )}

        <div className="container partner-hero-inner">
          <motion.h1
            className="partner-hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            {hero.title}
          </motion.h1>

          <motion.p
            className="partner-hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
          >
            <Cta cta={hero.cta} variant="light" />
          </motion.div>
        </div>

      </section>

      {/* ── Full-bleed photo strip (optional — a photo hero replaces it) ─── */}
      {strip && (
        <div className="partner-strip">
          <img
            src={strip.img}
            alt={strip.alt}
            style={strip.position ? { objectPosition: strip.position } : undefined}
          />
        </div>
      )}

      <div className="partner-canvas">
        {/* ── Why partner with us ───────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <motion.div className="partner-section-head" {...fadeUp}>
              <h2>{why.heading}</h2>
              {why.sub && <p>{why.sub}</p>}
            </motion.div>

            <div className="partner-why-grid">
              {why.items.map((item, i) => (
                <motion.div className="partner-why-item" key={item.title} custom={i} {...staggerUp}>
                  <span className="partner-why-badge">
                    <item.icon size={28} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Getting started ───────────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <div className="partner-split">
              <motion.div className="partner-split-photo" {...fadeUp}>
                <img src={gettingStarted.img} alt={gettingStarted.alt} />
              </motion.div>

              <motion.div
                className="partner-split-body"
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.1 }}
              >
                <h2>{gettingStarted.heading}</h2>
                <p>{gettingStarted.body}</p>
                <Cta cta={gettingStarted.cta} variant="blue" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Numbered steps ────────────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <motion.div className="partner-section-head" {...fadeUp}>
              <h2>{steps.heading}</h2>
              {steps.sub && <p>{steps.sub}</p>}
            </motion.div>

            <div className="partner-steps">
              {steps.items.map((step, i) => (
                <motion.div className="partner-step" key={step.title} custom={i} {...staggerUp}>
                  <span className="partner-step-num">{i + 1}</span>
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                  {step.link && (
                    <a
                      href={step.link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="partner-step-link"
                    >
                      {step.link.label}
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── What you need to apply ────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <motion.div className="partner-section-head" {...fadeUp}>
              <h2>{checklist.heading}</h2>
              {checklist.sub && <p>{checklist.sub}</p>}
            </motion.div>

            <div className="partner-checklist-grid">
              {checklist.groups.map((group, i) => (
                <motion.div
                  className="partner-checklist-card"
                  key={group.title}
                  custom={i}
                  {...staggerUp}
                >
                  <div className="partner-checklist-card-head">
                    <span>
                      <group.icon size={22} />
                    </span>
                    <h4>{group.title}</h4>
                  </div>
                  <ul>
                    {group.items.map((entry) => (
                      <li key={entry}>
                        <Check size={16} />
                        {entry}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── Highlight band ─────────────────────────────────────────────── */}
      <section className={`partner-band${band.bgImage ? " partner-band--photo" : ""}`}>
        {band.bgImage && (
          <>
            <img src={band.bgImage} alt="" aria-hidden="true" className="partner-band-bg" />
            <div className="partner-band-bg-overlay" />
          </>
        )}
        <div className="container">
          <motion.div className="partner-band-card" {...fadeUp}>
            <div>
              <span className="partner-band-kicker">{band.kicker}</span>
              <h2>{band.heading}</h2>
              <p>{band.body}</p>
              <ul className="partner-band-list">
                {band.bullets.map((bullet) => (
                  <li key={bullet}>
                    <Check size={16} />
                    {bullet}
                  </li>
                ))}
              </ul>
              <Cta cta={band.cta} variant="blue" />
            </div>
            <div className="partner-band-photo">
              <img src={band.img} alt={band.alt} />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="partner-canvas">
        {/* ── Feature cards ─────────────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <motion.div className="partner-section-head" {...fadeUp}>
              <h2>{cards.heading}</h2>
              {cards.sub && <p>{cards.sub}</p>}
            </motion.div>

            <div className="partner-card-grid">
              {cards.items.map((card, i) => (
                <motion.div className="partner-card" key={card.title} custom={i} {...staggerUp}>
                  <img src={card.img} alt={card.alt} className="partner-card-img" />
                  <div className="partner-card-body">
                    <h3>{card.title}</h3>
                    <p>{card.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <section className="partner-section">
          <div className="container">
            <motion.div className="partner-section-head" {...fadeUp}>
              <h2>{faq.heading}</h2>
              {faq.sub && <p>{faq.sub}</p>}
            </motion.div>

            <div className="partner-faq">
              {faq.items.map((item, i) => (
                <FaqItem
                  key={item.q}
                  item={item}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing contact block ─────────────────────────────────────── */}
        <section className="container partner-contact">
          <motion.div {...fadeUp}>
            <h2>{contact.heading}</h2>
            <p>{contact.body}</p>
            <Cta cta={contact.cta} variant="blue" />
          </motion.div>
        </section>
      </div>
    </div>
  );
}
